import { useState, useMemo, useContext } from 'react';
import PrivateLayout from '@/components/layout/auth/PrivateLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useI18n } from '@/hooks/useI18n';
import { useSharedLinks } from '@/lib/shared/hooks/useSharedLinks';
import { SharedLinksTable } from '@/lib/shared/SharedLinksTable';
import { SharedLinksTableSkeleton } from '@/lib/shared/SharedLinksTableSkeleton';
import { SharedLinkModal } from '@/lib/shared/SharedLinkModal';
import { SharedLink, CreateSharedLinkData, UpdateSharedLinkData } from '@/lib/shared/models/shared-link.model';
import { Plus, Search } from 'lucide-react';
import { AuthContext } from '@/lib/auth/models/auth.model';

type StatusFilter = 'all' | 'active' | 'expired';

const Shared = () => {
  const { t } = useI18n();
  const authContext = useContext(AuthContext);
  const userRole = authContext?.userRole;
  const userId = authContext?.userId;

  const {
    links,
    loading,
    createLink,
    updateLink,
    deleteLink,
  } = useSharedLinks();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [designerFilter, setDesignerFilter] = useState<string>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<SharedLink | null>(null);

  // Check if user can see designer filter (admin or agency)
  const canFilterByDesigner = userRole === 'admin' || userRole === 'agency';

  // Get unique designers for filter (if applicable)
  const designers = useMemo(() => {
    const uniqueDesigners = new Map<string, string>();
    links.forEach((link) => {
      if (!uniqueDesigners.has(link.designer_email)) {
        uniqueDesigners.set(link.designer_email, link.designer_name);
      }
    });
    return Array.from(uniqueDesigners.entries()).map(([email, name]) => ({
      email,
      name,
    }));
  }, [links]);

  // Filter links based on search and filters
  const filteredLinks = useMemo(() => {
    return links.filter((link) => {
      // Search filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        link.itinerary_name.toLowerCase().includes(searchLower) ||
        link.client_name?.toLowerCase().includes(searchLower) ||
        link.designer_name.toLowerCase().includes(searchLower);

      // Status filter
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && link.is_active) ||
        (statusFilter === 'expired' && !link.is_active);

      // Designer filter
      const matchesDesigner =
        designerFilter === 'all' || link.designer_email === designerFilter;

      return matchesSearch && matchesStatus && (canFilterByDesigner ? matchesDesigner : true);
    });
  }, [links, searchQuery, statusFilter, designerFilter, canFilterByDesigner]);

  const handleCreateLink = () => {
    setEditingLink(null);
    setModalOpen(true);
  };

  const handleEditLink = (link: SharedLink) => {
    setEditingLink(link);
    setModalOpen(true);
  };

  const handleModalSubmit = async (
    data: CreateSharedLinkData | UpdateSharedLinkData,
    linkId?: string
  ): Promise<boolean> => {
    if (linkId) {
      // Edit mode
      return await updateLink(linkId, data as UpdateSharedLinkData);
    } else {
      // Create mode
      return await createLink(data as CreateSharedLinkData);
    }
  };

  const handleDeleteLink = async (id: string) => {
    await deleteLink(id);
  };

  return (
    <PrivateLayout
      pageTitle={t("shared.title")}
      pageDescription={t("shared.description")}
      className="md:mt-[24px] sm:mt-[48px] mt-12"
      showBackButton={false}
      headerAction={
        <Button
          variant="default"
          size="lg"
          className="h-8 px-3"
          onClick={handleCreateLink}
        >
          <Plus className="h-4 w-4 mr-1" />
          {t("shared.createLink")}
        </Button>
      }
    >
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        {/* Filters */}
        <div className="bg-white rounded-lg border p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("common.search")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <Select
              value={statusFilter}
              onValueChange={(value: StatusFilter) => setStatusFilter(value)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("shared.status")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("shared.allStatus")}</SelectItem>
                <SelectItem value="active">{t("shared.active")}</SelectItem>
                <SelectItem value="expired">{t("shared.expired")}</SelectItem>
              </SelectContent>
            </Select>

            {/* Designer Filter - Only for admin/agency */}
            {canFilterByDesigner && (
              <Select value={designerFilter} onValueChange={setDesignerFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={t("shared.designer")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t("shared.allDesigners")}
                  </SelectItem>
                  {designers.map((designer) => (
                    <SelectItem key={designer.email} value={designer.email}>
                      {designer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border">
          {loading ? (
            <SharedLinksTableSkeleton />
          ) : (
            <SharedLinksTable
              links={filteredLinks}
              onEdit={handleEditLink}
              onDelete={handleDeleteLink}
            />
          )}
        </div>

        {/* Modal */}
        <SharedLinkModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSubmit={handleModalSubmit}
          editingLink={editingLink}
        />
      </div>
    </PrivateLayout>
  );
};

export default Shared;
